import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from "@angular/core";

import {DateRangeType, IDateRangeExtended} from "../date.range.picker/date.range.picker.interface";
import {LABEL_SUFFIX, TranslateService} from "../../../translate";
import * as moment from "moment";
import * as _ from "lodash";
import {KendoDatetimeLocaleService} from "../../services/kendo.datetime.service";
import {SystemConfigurationValues} from "../../../configuration/services/system.configuration.utils";

@Component({
	selector:'cp-date-range-extended-picker',
	templateUrl: './date.range.extended.picker.template.html',
	styleUrls: ['./date.range.extended.picker.style.scss'],
	changeDetection:ChangeDetectionStrategy.OnPush
})
export class DateRangeExtendedPickerComponent implements OnInit, OnChanges {
	@Input() dateRange: IDateRangeExtended;
	@Input() dateType: string = 'Date';  //Date, DateTime
	@Input() rangeType: DateRangeType = DateRangeType.Fixed;
	@Input() startDateForRelativeDate: Date;
	@Input() noStartDateSelectedText: string;
	@Input() rangeIsMandatory: boolean;

	@Output() onChange: EventEmitter<IDateRangeExtended> = new EventEmitter<IDateRangeExtended>(false);

	public duration: number;
	public durationScale: { name: string, description: string };
	public start: number;
	public startScale: { name: string, description: string };
	public exampleLabel: string;
	public scaleTypes: { name: string, description: string }[] = [];

	public last: number;
	public lastScale: { name: string, description: string };
	public includingCurrent: boolean;
	public includingCurrentScale: string;

	public fromDate: Date;
	public toDate: Date;

	public baseDateFactor: Date;
	public secondDateFactor:Date;
	public firstDateFactor: Date;
	public firstDayOfWeek;

	public scale: { name: string, description: string }[] = [
		{ name: "Hours", description:  <string>this.translateService.instant('Hours' + LABEL_SUFFIX) },
		{ name: "Days", description: <string>this.translateService.instant('Days' + LABEL_SUFFIX) },
		{ name: "Weeks", description: <string>this.translateService.instant('Weeks' + LABEL_SUFFIX) },
		{ name: "Months", description: <string>this.translateService.instant('Months' + LABEL_SUFFIX) },
		{ name: "Years", description: <string>this.translateService.instant('Years' + LABEL_SUFFIX) }];

	constructor(private cd: ChangeDetectorRef,
				private translateService:TranslateService,
				private datetimeFormatService:KendoDatetimeLocaleService,
				private systemConfigValues: SystemConfigurationValues,) {
		let weekendDays = this.systemConfigValues.getNumberArray("WeekendIntValues", "0,6");
		this.firstDayOfWeek = [0,1,2,3,4,5,6].findIndex((day) => !weekendDays.includes(day));

		this.baseDateFactor = moment('01/01/0001 00:00:00', "MM/DD/YYYY HH:mm:ss").toDate();
		this.duration = 1;
		this.durationScale = this.scale[1];
		this.start = 0;
		this.startScale = this.scale[1];
		this.last = 1;
		this.lastScale = this.scale[1];
	}

	ngOnChanges(changes: SimpleChanges){
		if (changes["dateRange"]) {
			if (this.dateRange.rangeType === DateRangeType.Relative || this.dateRange.rangeType === DateRangeType.TimeWindow) {
				if (this.dateRange.from !== this.firstDateFactor || this.dateRange.to !== this.secondDateFactor) {
					this.rangeType = this.dateRange.rangeType;
					this.secondDateFactor = null;
					this.firstDateFactor = null;
					this.populateControls();
					this.setIncludingCurrentScale();
					this.computeDateFactors(false);
					this.buildSimulateLabel();
					this.cd.markForCheck();
				}
			} else {
				this.rangeType = this.dateRange.rangeType;
				this.fromDate = this.dateRange.from;
				this.toDate = this.dateRange.to;
			}
		}

		if (changes["dateType"]) {
			this.buildScaleTypes();
		}

		if (changes["startDateForRelativeDate"]) {
			this.buildSimulateLabel();
			this.cd.markForCheck();
		}
	}

	ngOnInit(){
	}

	public onRangeTypeChanged() {
		if (this.rangeType === DateRangeType.Relative || this.rangeType === DateRangeType.TimeWindow) {
			this.computeDateFactors();
			this.buildSimulateLabel();
			if (this.rangeType === DateRangeType.TimeWindow) {
				this.setIncludingCurrentScale();
			}
			this.cd.markForCheck();
		} else {
			this.onChange.emit({from: this.fromDate, to: this.toDate, rangeType: this.rangeType});
		}
	}

	public computeDateFactors(fireChangeEvent = true) {
		if (this.rangeType === DateRangeType.Relative) {
			this.computeRelativeDateFactors(fireChangeEvent);
		} else if (this.rangeType === DateRangeType.TimeWindow) {
			this.computeTimeWindowDateFactors(fireChangeEvent);
		}
	}

	public buildSimulateLabel() {
		if (this.rangeType === DateRangeType.Relative) {
			this.buildRelativeDatesSimulateLabel();
		} else if (this.rangeType === DateRangeType.TimeWindow) {
			this.buildTimeWindowSimulateLabel();
		}
	}

	public computeRelativeDateFactors(fireChangeEvent = true) {
		if (!this.durationScale || !this.startScale) {
			return;
		}

		this.secondDateFactor = _.cloneDeep(this.baseDateFactor);
		switch (this.durationScale.name) {
			case 'Hours':
				this.secondDateFactor.setHours(this.secondDateFactor.getHours() + this.duration);
				break;
			case 'Days':
				this.secondDateFactor.setDate(this.secondDateFactor.getDate() + this.duration);
				break;
			case 'Weeks':
				this.secondDateFactor.setDate(this.secondDateFactor.getDate() + this.duration * 7);
				break;
			case 'Months':
				this.secondDateFactor.setMonth(this.secondDateFactor.getMonth() + this.duration);
				break;
			case 'Years':
				this.secondDateFactor.setFullYear(this.secondDateFactor.getFullYear() + this.duration);
				break;
		}

		this.firstDateFactor = _.cloneDeep(this.baseDateFactor);
		if (this.start > 0) {
			switch (this.startScale.name) {
				case 'Hours':
					this.firstDateFactor.setHours(this.firstDateFactor.getHours() + this.start);
					break;
				case 'Days':
					this.firstDateFactor.setDate(this.firstDateFactor.getDate() + this.start);
					break;
				case 'Weeks':
					this.firstDateFactor.setDate(this.firstDateFactor.getDate() + (this.start * 7));
					break;
				case 'Months':
					this.firstDateFactor.setMonth(this.firstDateFactor.getMonth() + this.start);
					break;
				case 'Years':
					this.firstDateFactor.setFullYear(this.firstDateFactor.getFullYear() + this.start);
					break;
			}
		}

		if (fireChangeEvent) {
			this.onChange.emit({from: this.firstDateFactor, to: this.secondDateFactor, rangeType: this.rangeType});
		}
	}

	public computeTimeWindowDateFactors(fireChangeEvent = true) {
		if (!this.lastScale) {
			return;
		}

		this.secondDateFactor = _.cloneDeep(this.baseDateFactor);
		switch (this.lastScale.name) {
			case 'Hours':
				this.secondDateFactor.setHours(0);
				break;
			case 'Days':
				this.secondDateFactor.setHours(1);
				break;
			case 'Weeks':
				this.secondDateFactor.setHours(2);
				break;
			case 'Months':
				this.secondDateFactor.setHours(3);
				break;
			case 'Years':
				this.secondDateFactor.setHours(4);
				break;
		}

		if (this.includingCurrent) {
			this.secondDateFactor.setMinutes(1);
		}

		this.firstDateFactor = _.cloneDeep(this.baseDateFactor);
		if (this.last > 0) {
			switch (this.lastScale.name) {
				case 'Hours':
					this.firstDateFactor.setHours(this.firstDateFactor.getHours() + this.last);
					break;
				case 'Days':
					this.firstDateFactor.setDate(this.firstDateFactor.getDate() + this.last);
					break;
				case 'Weeks':
					this.firstDateFactor.setDate(this.firstDateFactor.getDate() + (this.last * 7));
					break;
				case 'Months':
					this.firstDateFactor.setMonth(this.firstDateFactor.getMonth() + this.last);
					break;
				case 'Years':
					this.firstDateFactor.setFullYear(this.firstDateFactor.getFullYear() + this.last);
					break;
			}
		}

		if (fireChangeEvent) {
			this.onChange.emit({from: this.firstDateFactor, to: this.secondDateFactor, rangeType: this.rangeType});
		}
	}

	public buildRelativeDatesSimulateLabel() {
		this.exampleLabel = undefined;

		if (!this.startDateForRelativeDate || !this.firstDateFactor || !this.secondDateFactor) {
			return;
		}

		let startDate = this.computeDateTime(this.startDateForRelativeDate, this.firstDateFactor, -1);
		let endDate = this.computeDateTime(startDate, this.secondDateFactor, 1);

		if (this.startScale.name !== 'Hours' && this.durationScale.name  !== 'Hours') {
			startDate.setHours(0);
			startDate.setMinutes(0);
			startDate.setSeconds(0);

			endDate.setHours(0);
			endDate.setMinutes(0);
			endDate.setSeconds(-1);
		}

		this.exampleLabel = <string>this.translateService.instant('ValuesWillBeComputed' + LABEL_SUFFIX)  + ' ' +
							this.datetimeFormatService.datetime(this.startDateForRelativeDate) + ':' + '<br/>' +
							<string>this.translateService.instant('Start' + LABEL_SUFFIX) + ': ' +
							this.datetimeFormatService.datetime(startDate) + '  ' +
							<string>this.translateService.instant('End' + LABEL_SUFFIX) + ': ' +
							this.datetimeFormatService.datetime(endDate);

		this.cd.markForCheck();
	}

	public buildTimeWindowSimulateLabel() {
		this.exampleLabel = undefined;

		if (!this.startDateForRelativeDate || !this.firstDateFactor || !this.secondDateFactor) {
			return;
		}

		let endDate = this.computeTimeWindowEndDateTime(this.startDateForRelativeDate, this.secondDateFactor);
		let startDate = this.computeDateTime(endDate, this.firstDateFactor, -1);

		if (this.secondDateFactor.getHours() !== 0) { // nullify hours when not hours scale
			startDate.setHours(0);

			endDate.setHours(0);
		}

		startDate.setMinutes(0);
		startDate.setSeconds(0);

		endDate.setMinutes(0);
		endDate.setSeconds(-1);

		this.exampleLabel = <string>this.translateService.instant('ValuesWillBeComputed' + LABEL_SUFFIX)  + ' ' +
			this.datetimeFormatService.datetime(this.startDateForRelativeDate) + ':' + '<br/>' +
			<string>this.translateService.instant('Start' + LABEL_SUFFIX) + ': ' +
			this.datetimeFormatService.datetime(startDate) + '  ' +
			<string>this.translateService.instant('End' + LABEL_SUFFIX) + ': ' +
			this.datetimeFormatService.datetime(endDate);

		this.cd.markForCheck();
	}

	public setIncludingCurrentScale() {
		switch (this.lastScale.name) {
			case 'Hours':
				this.includingCurrentScale = 'hour';
				break;
			case 'Days':
				this.includingCurrentScale = 'day';
				break;
			case 'Weeks':
				this.includingCurrentScale = 'week';
				break;
			case 'Months':
				this.includingCurrentScale = 'month';
				break;
			case 'Years':
				this.includingCurrentScale = 'year';
				break;
		}
	}

	private populateControls() {
		if (this.rangeType === DateRangeType.Relative) {
			this.populateRelativeControls();
		} else if (this.rangeType === DateRangeType.TimeWindow) {
			this.populateTimeWindowControls();
		}
	}

	private populateRelativeControls() {
		this.duration = 1;
		this.durationScale = this.scale[1];
		this.start = 0;
		this.startScale = this.scale[1];

		if (this.dateRange.from) {
			let start = this.parseRelativeDate(this.dateRange.from);
			this.start = start.interval;
			this.startScale = this.scale.find(r => r.name === start.scale);
		}

		if (this.dateRange.to) {
			let duration = this.parseRelativeDate(this.dateRange.to);
			this.duration = duration.interval;
			this.durationScale = this.scale.find(r => r.name === duration.scale);
		}
	}

	private populateTimeWindowControls() {
		this.last = 1;
		this.lastScale = this.scale[1];
		this.includingCurrent = false;

		if (this.dateRange.to && this.dateRange.from) {
			this.includingCurrent = this.dateRange.to.getMinutes() === 1;
			let scale =  this.scale[this.dateRange.to.getHours()];
			this.last = this.parseRelativeDate(this.dateRange.from, scale.name).interval;
			this.lastScale = scale;
		}
	}

	private parseRelativeDate(date: Date, suggestedScale?: string) {
		let interval = 0;
		let scale: string;

		let hours = date.getHours();
		let days = date.getDate() - 1;
		let months = date.getMonth();
		let years = date.getFullYear() - 1;

		if (years > 0) {
			if (months === 0 && days === 0) {
				interval = years;
				scale = 'Years';
			} else if (months > 0 || days > 0) {
				if (months > 0 && days === 0) {
					interval = years * 12 + months;
					scale = 'Months';
				} else {
					let totalDays = Math.ceil((date.getTime() - this.baseDateFactor.getTime()) / (1000 * 3600 * 24));
					if (totalDays % 7 === 0) {
						interval = totalDays / 7;
						scale = 'Weeks';
					} else {
						interval = totalDays;
						scale = 'Days';
					}
				}
			}
		} else if (months > 0) {
			if (days === 0) {
				interval = months;
				scale = 'Months';
			} else {
				let totalDays = Math.ceil((date.getTime() - this.baseDateFactor.getTime()) / (1000 * 3600 * 24));
				if (totalDays % 7 === 0) {
					if (!suggestedScale || suggestedScale === 'Weeks') {
						interval = totalDays / 7;
						scale = 'Weeks';
					} else {
						interval = totalDays;
						scale = 'Days';
					}
				} else {
					interval = totalDays;
					scale = 'Days';
				}
			}
		} else if (days > 0) {
			if (hours === 0) {
				if (days % 7 === 0) {
					if (!suggestedScale || suggestedScale === 'Weeks') {
						interval = days / 7;
						scale = 'Weeks';
					} else {
						interval = days;
						scale = 'Days';
					}
				} else {
					interval = days;
					scale = 'Days';
				}
			} else {
				let totalHours = Math.ceil((date.getTime() - this.baseDateFactor.getTime()) / (1000 * 3600));
				interval = totalHours;
				scale = 'Hours';
			}
		} else {
			interval = hours;
			scale = 'Hours';
		}

		return {interval: interval, scale: scale};
	}

	private computeDateTime(baseDate: Date, dateFactor: Date, multiplier: number) {
		let baseDateCloned = _.cloneDeep(baseDate);
		baseDateCloned.setFullYear(baseDateCloned.getFullYear() + ((dateFactor.getFullYear() - 1) * multiplier));
		baseDateCloned.setMonth(baseDateCloned.getMonth() + ((dateFactor.getMonth()) * multiplier));
		baseDateCloned.setDate(baseDateCloned.getDate() + ((dateFactor.getDate() - 1) * multiplier));
		baseDateCloned.setHours(baseDateCloned.getHours() + (dateFactor.getHours() * multiplier));
		return baseDateCloned;
	}

	private computeTimeWindowEndDateTime(baseDate: Date, dateFactor: Date) {
		let baseDateCloned = _.cloneDeep(baseDate);

		let includingCurrent = dateFactor.getMinutes() === 1;
		let lastScale = dateFactor.getHours();

		switch (lastScale) {
			case 0: //Hours
				if (includingCurrent) {
					baseDateCloned.setHours(baseDateCloned.getHours() + 1);
				}
				break;
			case 1: //Days
				if (includingCurrent) {
					baseDateCloned.setDate(baseDateCloned.getDate() + 1);
				}
				break;
			case 2: //Weeks
				if (baseDateCloned.getDay() - this.firstDayOfWeek >= 0) {
					baseDateCloned.setDate(baseDateCloned.getDate() - (baseDateCloned.getDay() - this.firstDayOfWeek));
				} else {
					baseDateCloned.setDate(baseDateCloned.getDate() - 7 - (baseDateCloned.getDay() - this.firstDayOfWeek));
				}
				if (includingCurrent) {
					baseDateCloned.setDate(baseDateCloned.getDate() + 7);
				}
				break;
			case 3: //Months
				baseDateCloned.setDate(1);
				if (includingCurrent) {
					baseDateCloned.setMonth(baseDateCloned.getMonth() + 1);
				}
				break;
			case 4: //Years
				baseDateCloned.setDate(1);
				baseDateCloned.setMonth(0);
				if (includingCurrent) {
					baseDateCloned.setFullYear(baseDateCloned.getFullYear() + 1);
				}
				break;
		}
		return baseDateCloned;
	}

	private buildScaleTypes() {
		this.scaleTypes = [];
		if (this.dateType === 'DateTime') {
			this.scaleTypes.push(this.scale[0]);
		}
		this.scaleTypes.push(...this.scale.slice(1));
	}
}

